// src/modules/app-views/offers/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FormOutlined, FileTextOutlined } from '@ant-design/icons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import OffersRecordTab from 'modules/app-views/offers/pages/Document/tabs/RecordTab';
import OffersScriptTab from 'modules/app-views/offers/pages/Document/tabs/ScriptTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;
const TAB_LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 6 };
const STYLES = {
  cardHeader: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OffersDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name', 'type', 'shortDescription'];

  const tabsItems = useMemo(() => {
    return Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      viewMode,
      data,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const offerId = values?._id || allValues?._id || data?._id;
      const dataToSave = { ...values };

      await upsert({ dataSet: dataToSave, _id: offerId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeader}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OffersDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, data } = props;

  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      label: (
        <span style={TAB_LABEL_STYLE}>
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <OffersRecordTab {...props} tabName={initTab} />,
    },
    // {
    //   key: 'script',
    //   label: (
    //     <span style={TAB_LABEL_STYLE}>
    //       <FileTextOutlined />
    //       {translateX('operational_script')}
    //     </span>
    //   ),
    //   hide: false,
    //   disabled: isNewDocument,
    //   children: <OffersScriptTab {...props} tabName="script" />,
    // },
  ];
}
